import { eventBus, EventTypes, createEventPayload } from './EventBus';
import { DataValidator, type Workflow, type Project, type Ticket } from './DataValidation';

export interface WorkflowStep {
  id: string;
  name: string;
  type: 'action' | 'decision' | 'wait' | 'notification';
  order: number;
  conditions?: Record<string, any>;
  actions: WorkflowAction[];
  completed: boolean;
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
}

export interface WorkflowAction {
  type: string;
  target: string;
  parameters?: Record<string, any>;
  executed: boolean;
  result?: any;
  error?: string;
}

export interface WorkflowInstance {
  id: string;
  workflowId: string;
  status: 'running' | 'paused' | 'completed' | 'failed' | 'cancelled';
  currentStep: number;
  steps: WorkflowStep[];
  context: Record<string, any>;
  startedAt: Date;
  updatedAt: Date;
  completedAt?: Date;
  error?: string;
}

export interface WorkflowTrigger {
  eventType: string;
  conditions?: Record<string, any>;
  workflowId: string;
  enabled: boolean;
}

export class WorkflowEngine {
  private static instance: WorkflowEngine;
  private workflows: Map<string, Workflow> = new Map();
  private instances: Map<string, WorkflowInstance> = new Map();
  private triggers: Map<string, WorkflowTrigger> = new Map();
  private subscriptions: string[] = [];

  private constructor() {
    this.initializeEventListeners();
  }

  static getInstance(): WorkflowEngine {
    if (!WorkflowEngine.instance) {
      WorkflowEngine.instance = new WorkflowEngine();
    }
    return WorkflowEngine.instance;
  }

  // Initialize event listeners for workflow triggers
  private initializeEventListeners(): void {
    // Listen for project events
    this.subscriptions.push(
      eventBus.subscribe(EventTypes.PROJECT_CREATED, (payload) => {
        this.handleProjectCreated(payload);
      })
    );

    this.subscriptions.push(
      eventBus.subscribe(EventTypes.PROJECT_UPDATED, (payload) => {
        this.handleProjectUpdated(payload);
      })
    );

    // Listen for support events
    this.subscriptions.push(
      eventBus.subscribe(EventTypes.TICKET_CREATED, (payload) => {
        this.handleTicketCreated(payload);
      })
    );

    this.subscriptions.push(
      eventBus.subscribe(EventTypes.TICKET_ESCALATED, (payload) => {
        this.handleTicketEscalated(payload);
      })
    );

    // Listen for cross-system events
    this.subscriptions.push(
      eventBus.subscribe(EventTypes.WORKFLOW_TRIGGER, (payload) => {
        this.handleWorkflowTrigger(payload);
      })
    );
  }

  // Register a new workflow
  registerWorkflow(workflow: Workflow): void {
    try {
      const validatedWorkflow = DataValidator.validateWorkflow(workflow);
      this.workflows.set(workflow.id, validatedWorkflow);
      console.log(`[WorkflowEngine] Registered workflow: ${workflow.name}`);
    } catch (error) {
      console.error(`[WorkflowEngine] Failed to register workflow: ${workflow.name}`, error);
    }
  }

  // Create a workflow instance
  createInstance(workflowId: string, context: Record<string, any>): WorkflowInstance | null {
    const workflow = this.workflows.get(workflowId);
    if (!workflow) {
      console.error(`[WorkflowEngine] Workflow not found: ${workflowId}`);
      return null;
    }

    const instance: WorkflowInstance = {
      id: `${workflowId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      workflowId,
      status: 'running',
      currentStep: 0,
      steps: workflow.steps.map(step => ({
        ...step,
        completed: false,
        executed: false
      })),
      context,
      startedAt: new Date(),
      updatedAt: new Date()
    };

    this.instances.set(instance.id, instance);
    console.log(`[WorkflowEngine] Created instance: ${instance.id}`);
    
    // Start execution
    this.executeStep(instance.id);
    
    return instance;
  }

  // Execute a workflow step
  private async executeStep(instanceId: string): Promise<void> {
    const instance = this.instances.get(instanceId);
    if (!instance || instance.status !== 'running') {
      return;
    }

    const currentStep = instance.steps[instance.currentStep];
    if (!currentStep) {
      this.completeInstance(instanceId);
      return;
    }

    try {
      currentStep.startedAt = new Date();
      console.log(`[WorkflowEngine] Executing step: ${currentStep.name} in instance ${instanceId}`);

      // Check conditions
      if (currentStep.conditions && !this.evaluateConditions(currentStep.conditions, instance.context)) {
        console.log(`[WorkflowEngine] Conditions not met for step: ${currentStep.name}`);
        this.skipStep(instanceId);
        return;
      }

      // Execute actions
      for (const action of currentStep.actions) {
        await this.executeAction(action, instance.context);
      }

      // Mark step as completed
      currentStep.completed = true;
      currentStep.completedAt = new Date();
      instance.currentStep++;
      instance.updatedAt = new Date();

      // Continue to next step
      setTimeout(() => this.executeStep(instanceId), 100);
    } catch (error) {
      console.error(`[WorkflowEngine] Error executing step: ${currentStep.name}`, error);
      currentStep.error = error instanceof Error ? error.message : 'Unknown error';
      this.failInstance(instanceId, currentStep.error);
    }
  }

  // Execute a workflow action
  private async executeAction(action: WorkflowAction, context: Record<string, any>): Promise<void> {
    try {
      console.log(`[WorkflowEngine] Executing action: ${action.type} -> ${action.target}`);
      
      switch (action.type) {
        case 'create_ticket':
          await this.createSupportTicket(action.parameters, context);
          break;
        case 'update_project':
          await this.updateProject(action.parameters, context);
          break;
        case 'send_notification':
          await this.sendNotification(action.parameters, context);
          break;
        case 'escalate_project':
          await this.escalateProject(action.parameters, context);
          break;
        case 'sync_data':
          await this.syncData(action.parameters, context);
          break;
        default:
          console.warn(`[WorkflowEngine] Unknown action type: ${action.type}`);
      }

      action.executed = true;
      action.result = { success: true };
    } catch (error) {
      action.executed = true;
      action.error = error instanceof Error ? error.message : 'Unknown error';
      action.result = { success: false, error: action.error };
      throw error;
    }
  }

  // Evaluate workflow conditions
  private evaluateConditions(conditions: Record<string, any>, context: Record<string, any>): boolean {
    for (const [key, expectedValue] of Object.entries(conditions)) {
      const actualValue = context[key];
      if (actualValue !== expectedValue) {
        return false;
      }
    }
    return true;
  }

  // Skip a workflow step
  private skipStep(instanceId: string): void {
    const instance = this.instances.get(instanceId);
    if (instance) {
      instance.currentStep++;
      instance.updatedAt = new Date();
      this.executeStep(instanceId);
    }
  }

  // Complete a workflow instance
  private completeInstance(instanceId: string): void {
    const instance = this.instances.get(instanceId);
    if (instance) {
      instance.status = 'completed';
      instance.completedAt = new Date();
      instance.updatedAt = new Date();
      console.log(`[WorkflowEngine] Completed instance: ${instanceId}`);
      
      // Publish completion event
      eventBus.publish(EventTypes.WORKFLOW_TRIGGER, createEventPayload(
        'workflow-engine',
        { instanceId, status: 'completed' },
        instance.context.userId,
        instance.context.tenantId
      ));
    }
  }

  // Fail a workflow instance
  private failInstance(instanceId: string, error: string): void {
    const instance = this.instances.get(instanceId);
    if (instance) {
      instance.status = 'failed';
      instance.error = error;
      instance.updatedAt = new Date();
      console.error(`[WorkflowEngine] Failed instance: ${instanceId}`, error);
    }
  }

  // Action implementations
  private async createSupportTicket(parameters: any, context: Record<string, any>): Promise<void> {
    // Implementation for creating support tickets
    console.log(`[WorkflowEngine] Creating support ticket with parameters:`, parameters);
    // This would integrate with the Support Bridge system
  }

  private async updateProject(parameters: any, context: Record<string, any>): Promise<void> {
    // Implementation for updating projects
    console.log(`[WorkflowEngine] Updating project with parameters:`, parameters);
    // This would integrate with the Control Bridge system
  }

  private async sendNotification(parameters: any, context: Record<string, any>): Promise<void> {
    // Implementation for sending notifications
    console.log(`[WorkflowEngine] Sending notification with parameters:`, parameters);
  }

  private async escalateProject(parameters: any, context: Record<string, any>): Promise<void> {
    // Implementation for escalating projects
    console.log(`[WorkflowEngine] Escalating project with parameters:`, parameters);
  }

  private async syncData(parameters: any, context: Record<string, any>): Promise<void> {
    // Implementation for syncing data between systems
    console.log(`[WorkflowEngine] Syncing data with parameters:`, parameters);
  }

  // Event handlers
  private handleProjectCreated(payload: any): void {
    this.triggerWorkflows('project:created', payload);
  }

  private handleProjectUpdated(payload: any): void {
    this.triggerWorkflows('project:updated', payload);
  }

  private handleTicketCreated(payload: any): void {
    this.triggerWorkflows('ticket:created', payload);
  }

  private handleTicketEscalated(payload: any): void {
    this.triggerWorkflows('ticket:escalated', payload);
  }

  private handleWorkflowTrigger(payload: any): void {
    this.triggerWorkflows(payload.data.eventType, payload);
  }

  // Trigger workflows based on events
  private triggerWorkflows(eventType: string, payload: any): void {
    for (const [triggerId, trigger] of this.triggers.entries()) {
      if (trigger.eventType === eventType && trigger.enabled) {
        if (!trigger.conditions || this.evaluateConditions(trigger.conditions, payload.data)) {
          console.log(`[WorkflowEngine] Triggering workflow: ${trigger.workflowId} from event: ${eventType}`);
          this.createInstance(trigger.workflowId, {
            ...payload.data,
            userId: payload.userId,
            tenantId: payload.tenantId,
            triggerEvent: eventType,
            triggerId
          });
        }
      }
    }
  }

  // Get workflow instance
  getInstance(instanceId: string): WorkflowInstance | undefined {
    return this.instances.get(instanceId);
  }

  // Get all instances
  getAllInstances(): WorkflowInstance[] {
    return Array.from(this.instances.values());
  }

  // Pause workflow instance
  pauseInstance(instanceId: string): boolean {
    const instance = this.instances.get(instanceId);
    if (instance && instance.status === 'running') {
      instance.status = 'paused';
      instance.updatedAt = new Date();
      return true;
    }
    return false;
  }

  // Resume workflow instance
  resumeInstance(instanceId: string): boolean {
    const instance = this.instances.get(instanceId);
    if (instance && instance.status === 'paused') {
      instance.status = 'running';
      instance.updatedAt = new Date();
      this.executeStep(instanceId);
      return true;
    }
    return false;
  }

  // Cancel workflow instance
  cancelInstance(instanceId: string): boolean {
    const instance = this.instances.get(instanceId);
    if (instance && instance.status !== 'completed' && instance.status !== 'cancelled') {
      instance.status = 'cancelled';
      instance.updatedAt = new Date();
      return true;
    }
    return false;
  }

  // Cleanup
  destroy(): void {
    this.subscriptions.forEach(subId => {
      eventBus.unsubscribe(subId);
    });
    this.subscriptions = [];
    this.workflows.clear();
    this.instances.clear();
    this.triggers.clear();
  }
}

// Export singleton instance
export const workflowEngine = WorkflowEngine.getInstance();

// Predefined workflow templates
export const WorkflowTemplates = {
  // Project to Support handoff
  PROJECT_ESCALATION: {
    id: 'project-escalation',
    name: 'Project Escalation to Support',
    type: 'cross-system' as const,
    status: 'active' as const,
    steps: [
      {
        id: 'assess-escalation',
        name: 'Assess Escalation Need',
        type: 'decision' as const,
        order: 1,
        conditions: { escalationLevel: { $gte: 3 } },
        actions: []
      },
      {
        id: 'create-support-ticket',
        name: 'Create Support Ticket',
        type: 'action' as const,
        order: 2,
        actions: [
          {
            type: 'create_ticket',
            target: 'support-bridge',
            parameters: { priority: 'high', category: 'project-escalation' }
          }
        ]
      },
      {
        id: 'notify-stakeholders',
        name: 'Notify Stakeholders',
        type: 'action' as const,
        order: 3,
        actions: [
          {
            type: 'send_notification',
            target: 'stakeholders',
            parameters: { template: 'project-escalation' }
          }
        ]
      }
    ],
    triggers: [
      {
        eventType: EventTypes.PROJECT_UPDATED,
        conditions: { escalationLevel: { $gte: 3 } }
      }
    ]
  },

  // Support to Project handoff
  TICKET_PROJECT_LINK: {
    id: 'ticket-project-link',
    name: 'Link Support Ticket to Project',
    type: 'cross-system' as const,
    status: 'active' as const,
    steps: [
      {
        id: 'identify-project',
        name: 'Identify Related Project',
        type: 'action' as const,
        order: 1,
        actions: [
          {
            type: 'sync_data',
            target: 'project-system',
            parameters: { operation: 'find-related-project' }
          }
        ]
      },
      {
        id: 'update-project',
        name: 'Update Project Status',
        type: 'action' as const,
        order: 2,
        actions: [
          {
            type: 'update_project',
            target: 'project-system',
            parameters: { updateRisk: true, updateTimeline: true }
          }
        ]
      }
    ],
    triggers: [
      {
        eventType: EventTypes.TICKET_CREATED,
        conditions: { businessCritical: true }
      }
    ]
  }
};